import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Container,
  FormCheck,
  Modal,
  ProgressBar,
} from "react-bootstrap";
import {
  Check,
  ChevronDown,
  ChevronUp,
  Pen,
  PlusCircle,
  Trash,
  X,
} from "react-bootstrap-icons";
import { toast } from "react-toastify";
import EntityTable from "./EntityTable.js";
import AlertModal from "./AlertModal";
import Loader from "./Loader";
import FormGenerator from "../form-generator/FormGenerator";
import { makeRESTApiRequests } from "../helpers/api.js";

const getForm = ({ entityName, rows, type = "Add" }) => ({
  forms: [
    {
      name: `${type} ${entityName.toLowerCase()}`,
      markCompulsoryFields: true,
      hideFormName: true,
      compact: true,
      submit: {
        name: type,
        show: true,
        onSubmit:
          type === "Add" ? "onNewItemFormSubmit" : "onUpdateItemFormSubmit",
      },
      rows,
    },
  ],
});

const AddEditModal = ({
  show,
  toBeEditedItem,
  onHide,
  showProgress,
  addForm,
  updateForm,
  entityName,
  secondaryUpdateRows,
}) => {
  updateForm = useMemo(() => {
    if (secondaryUpdateRows && toBeEditedItem?.name === "Featured Videos") {
      return getForm({ entityName, rows: secondaryUpdateRows, type: "Update" });
    }
    return updateForm;
  }, [toBeEditedItem]);

  return (
    <>
      <Modal size="lg" show={show} onHide={onHide} centered backdrop="static">
        <Modal.Header closeButton={!showProgress}>
          <Modal.Title>
            <h6 className="mb-0">
              {toBeEditedItem
                ? `Update ${entityName}`
                : `Add New ${entityName}`}
            </h6>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="overflow-auto px-1">
          <FormGenerator
            formJson={toBeEditedItem ? updateForm : addForm}
            formValues={
              toBeEditedItem
                ? { [`Update ${entityName.toLowerCase()}`]: toBeEditedItem }
                : {}
            }
          />
          {showProgress && (
            <ProgressBar
              now={100}
              animated
              striped
              label={
                toBeEditedItem
                  ? `Updating ${entityName}...`
                  : `Saving ${entityName}....`
              }
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  );
};

/**
 *  example: 
 *  entityName: Manufacturer,
 *  entityKey: manufacturer,
    endpoint: /manufacturers
    formFields: 
 */
const EntityCRUD = ({
  entityName,
  endpoint,
  entityTableHeaders,
  entityTableSearchKeys,
  addFormRows = [],
  updateFormRows,
  onProgressChange,
  onEntityUpdate,
  entityAppChoiceKey,
  addNewButton = true,
  deleteButton = true,
  secondaryUpdateRows,
  groupBy,
  groupValues,
  showAdditionalHeader,
  prefillValues = [],
  showDelete = true,
  updateDetails,
  createDetails,
  otherActions = null,
}) => {
  const [loading, setLoading] = useState(false);

  const [items, setItems] = useState(prefillValues);
  const [filteredItems, setFilteredItems] = useState([]);
  const [showDisabledItems, setShowDisabledItems] = useState(false);

  const [deleteInProgress, setDeleteInProgress] = useState(false);
  const [toBeDeletedItem, setToBeDeletedItem] = useState(null);
  const [itemActionMeta, setItemActionMeta] = useState(null);
  const [itemAddingUpdating, setItemAddingUpdating] = useState(false);
  const [showGroupChildren, setShowGroupChildren] = useState({});
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const fetchItems = async () => {
    setLoading(true);

    const { response, error } = await makeRESTApiRequests({
      endpoint: `${endpoint}`,
      method: "POST",
    });

    setLoading(false);

    if (error) {
      toast.error(error);
      return;
    }

    setItems(response?.results || response);
  };

  useEffect(() => {
    fetchItems();
  }, []);

  useEffect(() => {
    setFilteredItems(
      items.filter(
        (i) =>
          showDisabledItems || !i.hasOwnProperty("isEnabled") || i.isEnabled
      )
    );
  }, [items, showDisabledItems]);

  useEffect(() => {
    onProgressChange &&
      onProgressChange(deleteInProgress || itemAddingUpdating);
  }, [deleteInProgress, itemAddingUpdating]);

  const deleteItem = async () => {
    setDeleteInProgress(true);

    const { response, error } = await makeRESTApiRequests({
      endpoint: `${endpoint}/${toBeDeletedItem._id}`,
      method: "DELETE",
    });

    setDeleteInProgress(false);
    if (error) {
      toast.error(error);
      return;
    }

    items.splice(items.findIndex((i) => i._id === toBeDeletedItem._id), 1);
    setItems([...items]);
    onEntityUpdate && onEntityUpdate(entityAppChoiceKey, entityName, items);
    setToBeDeletedItem(null);
  };

  const onNewItemFormSubmit = async (form) => {
    if (!form.hourlyWage) {
      form.hourlyWage = 0;
    }

    setItemAddingUpdating(true);

    const { response, error } = await makeRESTApiRequests({
      endpoint: createDetails?.endPoint || endpoint,
      requestBody: form,
    });

    setItemAddingUpdating(false);
    if (error) {
      toast.error(error);
      return;
    }

    const newItems = [response, ...items];
    setItems(newItems);
    onEntityUpdate && onEntityUpdate(entityAppChoiceKey, entityName, newItems);
    setItemActionMeta(null);
  };

  const onUpdateItemFormSubmit = async (form) => {
    if (!form.hourlyWage) {
      form.hourlyWage = 0;
    }

    setItemAddingUpdating(true);

    const { response, error } = await makeRESTApiRequests({
      endpoint:
        updateDetails?.endPoint || `${endpoint}/${itemActionMeta.item._id}`,
      requestBody: {
        ...form,
        email: itemActionMeta?.item?.email,
        isEnabled: form?.status === "Enable" ? true : false,
        status: undefined,
      },
      method: updateDetails?.method || "PUT",
    });

    setItemAddingUpdating(false);

    if (error) {
      toast.error(error);
      return;
    }

    const itemIndex = items.findIndex((i) => i._id === response._id);
    items[itemIndex] = { ...items[itemIndex], ...response };
    if (entityName === "Users") {
      let updatedItems = items?.map((item) => {
        return item;
      });
      setItems([...updatedItems]);
    } else {
      setItems([...items]);
    }

    onEntityUpdate && onEntityUpdate(entityAppChoiceKey, entityName, items);
    toast.success("Updated successfully");
    setItemActionMeta(null);
  };

  const updateStatus = async (item) => {
    setUpdatingStatus(true);
    const { response, error } = await makeRESTApiRequests({
      endpoint: updateDetails?.endPoint || `${endpoint}/${item._id}`,
      requestBody: { email: item?.email, isEnabled: !item?.isEnabled },
      method: updateDetails.method || "PUT",
    });

    if (error) {
      setUpdatingStatus(false);
      toast.error(error);
      return;
    }

    setUpdatingStatus(false);
    const itemIndex = items.findIndex((i) => i._id === response._id);
    items[itemIndex] = { ...items[itemIndex], ...response };
    setItems([...items]);

    toast.success("Status Updated Successfully.");
  };

  const onShowGroupChildrenChange = (key, value) => {
    showGroupChildren[key] = value;
    setShowGroupChildren({ ...showGroupChildren });
  };

  window["onNewItemFormSubmit"] = onNewItemFormSubmit;
  window["onUpdateItemFormSubmit"] = onUpdateItemFormSubmit;

  useEffect(() => {
    if (groupValues) {
      groupValues.forEach((groupValue) => {
        showGroupChildren[groupValue] = true;
      });
      showGroupChildren["Others"] = true;
    }
  }, [groupValues]);

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <Card>
        <Card.Body>
          <div className="d-flex">
            <div className="flex-grow-1">
              <h6 className="mb-0">{entityName}</h6>
            </div>
            <div>
              <FormCheck
                label={
                  <h6 className="mb-0 small-text">
                    Show Disabled {entityName}
                  </h6>
                }
                checked={showDisabledItems}
                onChange={(e) => setShowDisabledItems(!showDisabledItems)}
              />
            </div>
          </div>
          <hr className="my-3" />
          {loading ? (
            <Loader />
          ) : (
            items && (
              <>
                <EntityTable
                  rowKeyField={"_id"}
                  data={filteredItems}
                  headers={entityTableHeaders}
                  searchQueryKeys={entityTableSearchKeys}
                  groupBy={groupBy}
                  groupValues={groupValues}
                  toggleShowMoreSection={showGroupChildren}
                  additionalHeaderComponentForGroupValues={
                    showAdditionalHeader
                      ? (currentGroupValue) => {
                          return (
                            <>
                              <div className="w-100 d-flex justify-content-between align-items-center">
                                <div className="d-flex justify-content-start align-items-center">
                                  <h6 className="small-text my-0">
                                    {currentGroupValue || "Others"}
                                  </h6>
                                  {!showGroupChildren[currentGroupValue] ? (
                                    <ChevronUp
                                      className="ml-2 pointer mb-1"
                                      title="Show Less"
                                      onClick={() =>
                                        onShowGroupChildrenChange(
                                          currentGroupValue,
                                          true
                                        )
                                      }
                                      size={17}
                                    />
                                  ) : (
                                    <ChevronDown
                                      className="ml-2 pointer"
                                      title="Show More"
                                      onClick={() =>
                                        onShowGroupChildrenChange(
                                          currentGroupValue,
                                          false
                                        )
                                      }
                                      size={17}
                                    />
                                  )}
                                </div>
                              </div>
                            </>
                          );
                        }
                      : null
                  }
                  additionalComponents={() => {
                    return (
                      <>
                        {addNewButton && (
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => setItemActionMeta({ mode: "add" })}
                          >
                            <PlusCircle className="mr-1" /> New {entityName}
                          </Button>
                        )}
                      </>
                    );
                  }}
                  actionCell={(item) => (
                    <div>
                      <Button
                        variant="outline-primary"
                        className="px-1 py-0 mr-1"
                        onClick={() =>
                          setItemActionMeta({ item, mode: "edit" })
                        }
                      >
                        <Pen size={10} />
                      </Button>
                      {deleteButton && (
                        <>
                          <Button
                            title={
                              showDelete
                                ? "Delete"
                                : !item?.isEnabled
                                ? "Enable"
                                : "Disable"
                            }
                            variant={`outline-${
                              showDelete
                                ? "danger"
                                : item?.isEnabled
                                ? "warning"
                                : "success"
                            }`}
                            className="px-1 py-0 mr-1"
                            disabled={!showDelete && updatingStatus}
                            onClick={() =>
                              !showDelete
                                ? updateStatus(item)
                                : setToBeDeletedItem(item)
                            }
                          >
                            {!showDelete ? (
                              !item?.isEnabled ? (
                                <Check size={12} />
                              ) : (
                                <X size={12} />
                              )
                            ) : (
                              <Trash size={10} />
                            )}
                          </Button>
                        </>
                      )}
                      {otherActions && otherActions(item)}
                    </div>
                  )}
                />
                {items.length > 0 && (
                  <AlertModal
                    show={toBeDeletedItem !== null}
                    alertText={`Are you sure you want to delete this ${entityName.toLowerCase()}? This action cannot be undone!`}
                    onHide={() => setToBeDeletedItem(null)}
                    onDismissClick={() => setToBeDeletedItem(null)}
                    onContinueClick={deleteItem}
                    progressText={`Deleting ${entityName.toLowerCase()}...`}
                    showProgress={deleteInProgress}
                  />
                )}
                <AddEditModal
                  entityName={entityName}
                  show={itemActionMeta !== null}
                  onHide={() => setItemActionMeta(null)}
                  toBeEditedItem={itemActionMeta ? itemActionMeta.item : null}
                  showProgress={itemAddingUpdating}
                  addForm={getForm({ entityName, rows: addFormRows })}
                  updateForm={getForm({
                    entityName,
                    rows:
                      typeof updateFormRows === "function"
                        ? updateFormRows(itemActionMeta?.item)
                        : updateFormRows,
                    type: "Update",
                  })}
                  secondaryUpdateRows={secondaryUpdateRows}
                />
              </>
            )
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

export default EntityCRUD;
